import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export const useNotifications = () => {
  const [permission, setPermission] = useState('default');
  const [settings, setSettings] = useState({
    enabled: false,
    sound_enabled: true,
    messages: true,
    order_requests: true,
    order_notifications: true,
    forms: true
  });
  const [swRegistration, setSwRegistration] = useState(null);

  // Initialize service worker and check permission
  useEffect(() => {
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }

    // Register service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then((registration) => {
          console.log('Service Worker registered:', registration);
          setSwRegistration(registration);
        })
        .catch((error) => {
          console.error('Service Worker registration failed:', error);
        });
    }

    // Load settings from localStorage
    const savedSettings = localStorage.getItem('notificationSettings');
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  // Request notification permission
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) {
      console.log('This browser does not support notifications');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        setSettings(prev => {
          const newSettings = { ...prev, enabled: true };
          localStorage.setItem('notificationSettings', JSON.stringify(newSettings));
          return newSettings;
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, []);

  // Update notification settings
  const updateSettings = useCallback((newSettings) => {
    setSettings(prev => {
      const updated = { ...prev, ...newSettings };
      localStorage.setItem('notificationSettings', JSON.stringify(updated));
      
      // Also save to backend if user is logged in
      const token = localStorage.getItem('token');
      if (token) {
        axios.put(`${API}/user/notification-settings`, updated, getAuthHeader())
          .catch(err => console.error('Failed to save notification settings:', err));
      }
      
      return updated;
    });
  }, []);

  // Play notification sound using Web Audio API
  const playSound = useCallback(() => {
    if (!settings.sound_enabled) return;
    
    try {
      // Use Web Audio API to generate a pleasant notification sound
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create oscillator for the main tone
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      // Pleasant notification sound (two-tone chime)
      oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
      oscillator.frequency.setValueAtTime(1100, audioContext.currentTime + 0.1); // Higher pitch
      
      oscillator.type = 'sine';
      
      // Fade in and out
      gainNode.gain.setValueAtTime(0, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, audioContext.currentTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
      
      // Second tone for pleasant chime effect
      setTimeout(() => {
        const osc2 = audioContext.createOscillator();
        const gain2 = audioContext.createGain();
        
        osc2.connect(gain2);
        gain2.connect(audioContext.destination);
        
        osc2.frequency.setValueAtTime(1320, audioContext.currentTime); // E6
        osc2.type = 'sine';
        
        gain2.gain.setValueAtTime(0, audioContext.currentTime);
        gain2.gain.linearRampToValueAtTime(0.2, audioContext.currentTime + 0.05);
        gain2.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.25);
        
        osc2.start(audioContext.currentTime);
        osc2.stop(audioContext.currentTime + 0.25);
      }, 100);
      
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  }, [settings.sound_enabled]);

  // Show local notification (when app is in foreground)
  const showNotification = useCallback((title, options = {}) => {
    if (permission !== 'granted' || !settings.enabled) return;

    // Check if this notification type is enabled
    const type = options.type || 'messages';
    if (!settings[type]) return;

    // Play sound if enabled
    if (settings.sound_enabled) {
      playSound();
    }

    // Show browser notification
    if (swRegistration) {
      swRegistration.showNotification(title, {
        body: options.body || '',
        icon: options.icon || '/logo192.png',
        badge: '/logo192.png',
        tag: options.tag || 'default',
        data: options.data || { url: '/' },
        vibrate: [200, 100, 200],
        requireInteraction: options.requireInteraction || false
      });
    } else if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(title, {
        body: options.body || '',
        icon: options.icon || '/logo192.png',
        tag: options.tag || 'default'
      });
    }
  }, [permission, settings, swRegistration, playSound]);

  // Check if a specific notification type is enabled
  const isTypeEnabled = useCallback((type) => {
    return settings.enabled && settings[type];
  }, [settings]);

  return {
    permission,
    settings,
    swRegistration,
    requestPermission,
    updateSettings,
    showNotification,
    playSound,
    isTypeEnabled,
    isSupported: 'Notification' in window
  };
};

export default useNotifications;
